/**
 * Receipt photos for manual payments (Vodafone Cash, InstaPay, bank
 * transfer): the checks shared by the checkout modal and `/invoices/action`.
 *
 * Phone galleries hand the browser 3–12 MB camera photos, sometimes with an
 * empty MIME type, and iPhones may hand over HEIC. The modal shrinks big
 * photos before upload; the action re-checks with the same rules.
 */

/** Largest receipt the action accepts (after the modal shrank it). */
export const MAX_RECEIPT_IMAGE_BYTES = 10 * 1024 * 1024;

/** Photos above this size (or {@link RECEIPT_MAX_DIMENSION}) get re-encoded in the browser. */
export const RECEIPT_COMPRESS_ABOVE_BYTES = 1.5 * 1024 * 1024;

/** Longest side of a re-encoded receipt; enough to read a transaction number. */
export const RECEIPT_MAX_DIMENSION = 2000;

/** Why a receipt cannot be sent; each one has an `invoices.*` translation. */
export type ReceiptImageProblem =
  | "receiptRequired"
  | "receiptEmpty"
  | "receiptNotImage"
  | "receiptUnsupportedFormat"
  | "receiptTooLarge";

/** The parts of a `File` the checks read. */
export type ReceiptFileLike = {
  name?: string;
  type?: string;
  size: number;
};

const decodableImageExtensions = new Set([
  "jpg",
  "jpeg",
  "jfif",
  "png",
  "webp",
  "gif",
  "bmp",
]);

const heicExtensions = new Set(["heic", "heif"]);

/**
 * Types that say nothing about the content: a picker that gives no MIME type
 * makes the browser (and undici) send the part as `application/octet-stream`.
 */
const unknownMimeTypes = new Set(["", "application/octet-stream"]);

function extensionOf(name = "") {
  const dot = name.lastIndexOf(".");

  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

/**
 * Whether the file is an iPhone HEIC/HEIF photo (by MIME type or name).
 *
 * @example
 * isHeicReceipt({ name: "IMG_0001.HEIC", type: "", size: 1 }); // true
 */
export function isHeicReceipt(file: ReceiptFileLike) {
  const type = (file.type ?? "").toLowerCase();

  return (
    type === "image/heic" ||
    type === "image/heif" ||
    heicExtensions.has(extensionOf(file.name))
  );
}

/**
 * The first reason this receipt cannot be uploaded, or `undefined` when it
 * can. An empty / `application/octet-stream` MIME type is accepted when the
 * file name is a normal photo (some Android pickers give no type).
 *
 * @example
 * getReceiptImageProblem({ name: "1000977965.jpg", type: "", size: 300_000 }); // undefined
 * getReceiptImageProblem({ name: "a.jpg", type: "image/jpeg", size: 12 * 1024 * 1024 }); // "receiptTooLarge"
 */
export function getReceiptImageProblem(
  file: ReceiptFileLike | null | undefined,
  maxBytes = MAX_RECEIPT_IMAGE_BYTES,
): ReceiptImageProblem | undefined {
  if (!file) return "receiptRequired";

  if (!file.size) return "receiptEmpty";

  if (isHeicReceipt(file)) return "receiptUnsupportedFormat";

  const type = (file.type ?? "").toLowerCase();
  const looksLikeImage = unknownMimeTypes.has(type)
    ? decodableImageExtensions.has(extensionOf(file.name))
    : type.startsWith("image/");

  if (!looksLikeImage) return "receiptNotImage";

  if (file.size > maxBytes) return "receiptTooLarge";

  return undefined;
}

const mimeTypeByExtension: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jfif: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  bmp: "image/bmp",
};

/**
 * MIME type to forward the receipt with: its own image type, or one inferred
 * from the file name when the browser sent none (the API only treats a part
 * as an image when its MIME type says so).
 *
 * @example
 * receiptUploadMimeType({ name: "1000977965.jpg", type: "application/octet-stream", size: 1 }); // "image/jpeg"
 */
export function receiptUploadMimeType(file: ReceiptFileLike) {
  const type = (file.type ?? "").toLowerCase();

  if (!unknownMimeTypes.has(type)) return type;

  return mimeTypeByExtension[extensionOf(file.name)] ?? type;
}

/**
 * Translation key for a receipt problem (`invoices.<problem>`).
 *
 * @example
 * trans(receiptProblemTranslationKey("receiptTooLarge"));
 */
export function receiptProblemTranslationKey(problem: ReceiptImageProblem) {
  return `invoices.${problem}`;
}

/** Error `code` the action returns for a receipt problem. */
export function receiptProblemErrorCode(problem: ReceiptImageProblem) {
  return `invoices.${problem}`;
}

const receiptProblems: ReceiptImageProblem[] = [
  "receiptRequired",
  "receiptEmpty",
  "receiptNotImage",
  "receiptUnsupportedFormat",
  "receiptTooLarge",
];

/**
 * Map an action error `code` back to a receipt problem, so the browser shows
 * the message in the student's own language.
 *
 * @example
 * receiptProblemFromErrorCode("invoices.receiptTooLarge"); // "receiptTooLarge"
 */
export function receiptProblemFromErrorCode(
  code: unknown,
): ReceiptImageProblem | undefined {
  if (typeof code !== "string" || !code.startsWith("invoices.")) {
    return undefined;
  }

  const problem = code.slice("invoices.".length) as ReceiptImageProblem;

  return receiptProblems.includes(problem) ? problem : undefined;
}

/**
 * Whether the browser should re-encode this photo before upload.
 *
 * @example
 * shouldCompressReceipt({ size: 4_000_000, type: "image/jpeg" }, 4000, 3000); // true
 */
export function shouldCompressReceipt(
  file: ReceiptFileLike,
  width: number,
  height: number,
) {
  const type = (file.type ?? "").toLowerCase();

  // GIFs would lose their animation; they are tiny receipts anyway.
  if (type === "image/gif") return false;

  return (
    file.size > RECEIPT_COMPRESS_ABOVE_BYTES ||
    Math.max(width, height) > RECEIPT_MAX_DIMENSION
  );
}

/**
 * Target size that keeps the aspect ratio and fits {@link RECEIPT_MAX_DIMENSION}.
 *
 * @example
 * fitReceiptDimensions(4000, 3000); // { width: 2000, height: 1500 }
 */
export function fitReceiptDimensions(width: number, height: number) {
  const longest = Math.max(width, height);

  if (longest <= RECEIPT_MAX_DIMENSION) return { width, height };

  const scale = RECEIPT_MAX_DIMENSION / longest;

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * JPEG file name for a re-encoded receipt.
 *
 * @example
 * compressedReceiptName("1000977965.png"); // "1000977965.jpg"
 */
export function compressedReceiptName(name = "receipt") {
  const dot = name.lastIndexOf(".");
  const base = (dot > 0 ? name.slice(0, dot) : name) || "receipt";

  return `${base}.jpg`;
}
